import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';

import { GlossaryIndexPage } from '../../components/Glossary';
import Layout from '../../components/Layout';
import { imageOgStaticLoader } from '../../helpers/og-image';
import { GlossaryIndex, GlossaryItem } from '../../types/Glossary';
import { fetchGlossary, mapGlossaryTerms } from '../../utils/glossary';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../../utils/storyblok';
import { pageTranslations } from '../../utils/translations';

type PageProps = {
  locale: string;
  locales: string[];
  glossary: GlossaryItem[];
  page: GlossaryIndex;
  preview: boolean;
  ogImageUrl: string;
};

export async function getStaticProps({
  locale = 'en',
  locales = ['en'],
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<PageProps>> {
  const translations = await pageTranslations(locale, ['glossary', 'og']);

  const story: StoryResponse<GlossaryIndex> = await getStoryblokPage(
    'cdn/stories/glossary',
    {
      locale,
      preview,
      resolve_links: 'story',
    },
  );

  if (!story?.data.story) return { notFound: true };

  const glossary = await fetchGlossary({ locale, preview });

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: { key: 'og:glossary.index.title' },
    locale,
  });

  return {
    props: {
      glossary: mapGlossaryTerms(glossary),
      locale,
      locales,
      page: story.data.story,
      preview,
      ogImageUrl,
      ...translations,
    },
  };
}

const Page = ({
  glossary,
  locale,
  locales,
  ogImageUrl,
  page,
  preview,
}: PageProps) => {
  const index = useStoryblok(page, preview, locale);
  const { seo } = index.content;

  return (
    <Layout
      description={seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={seo?.title}
    >
      <GlossaryIndexPage glossary={glossary} locale={locale} page={index} />
    </Layout>
  );
};

export default Page;
